import React, { useCallback, useEffect, useState } from 'react';
import {
  recommendationsApi,
  ApartmentItemError,
  type ApartmentRecommendationItemDetail,
  type ApartmentRecommendationMode,
} from '../../services/api';
import { Button } from '../../ds/components/Button';
import { ModalShell } from '../../ds/ModalShell';
import { ConfirmModal } from '../../ds/ConfirmModal';
import { InputField } from '../../ds/InputField';
import { ROOM_OPTIONS } from './PgCogDetails';
import { DeviceOptionsEditor } from './DeviceOptionsEditor';

/**
 * Edytor katalogu zaleceń dla LOKALI (migracja 059) — OSOBNO od katalogu A/B/C/D budynku.
 *
 * Bronek, 2026-10-02/07/09: „edycja/tworzenie zaleceń dla lokali" + nowe pozycje i zmiany treści.
 * Pozycja = nazwa na liście, treść do protokołu i opcjonalne pola wyboru: urządzenia (lista
 * edytowana niżej, przed albo po treści) i pomieszczenia (podzbiór słownika, opcjonalnie „Inne").
 * To NIE jest kreator formularzy — tylko te dwa rodzaje pól.
 *
 * Usuwania NIE MA. Wycofana pozycja znika z listy na tablecie, a zapisane wcześniej zalecenia to
 * wolny tekst — zostają bez zmian i nadal się rozpoznają. Zmiana treści dopisuje poprzednią do
 * „wcześniejszych treści" (serwer, w tej samej transakcji), żeby stare zapisy dalej otwierały
 * się jako ta pozycja. Id nadaje serwer i już się nie zmienia.
 */

const MODE_LABELS: Record<ApartmentRecommendationMode, string> = {
  none: 'bez wyboru',
  optional: 'opcjonalny',
  required: 'wymagany',
};

interface FormState {
  /** null = nowa pozycja */
  id: string | null;
  label: string;
  baseText: string;
  devicesMode: ApartmentRecommendationMode;
  devicePosition: 'prefix' | 'suffix';
  allowOtherDevice: boolean;
  roomsMode: ApartmentRecommendationMode;
  /** null = wszystkie pomieszczenia słownika */
  roomIds: string[] | null;
  allowOtherRoom: boolean;
  legacyTexts: string[];
  builtinKind: string | null;
}

const emptyForm = (): FormState => ({
  id: null,
  label: '',
  baseText: '',
  devicesMode: 'none',
  devicePosition: 'prefix',
  allowOtherDevice: false,
  roomsMode: 'optional',
  roomIds: null,
  allowOtherRoom: false,
  legacyTexts: [],
  builtinKind: null,
});

const actionStyle: React.CSSProperties = {
  minHeight: 'var(--touch-min)',
  padding: '0 var(--space-3)',
  flexShrink: 0,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 'var(--space-2)',
  fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-secondary)',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 'var(--touch-min)',
  padding: '0 var(--space-4)',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-body)',
  color: 'var(--text-primary)',
  background: 'var(--surface-card)',
  border: 'var(--border-width-strong) solid var(--border-strong)',
  borderRadius: 'var(--radius-md)',
};

const checkRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: 'var(--touch-min)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
};

/** Treść nie może mieć nowej linii (bloki zaleceń rozdziela pusta linia, pomieszczenia — wiersz). */
const singleLine = (v: string) => v.replace(/[\r\n]+/g, ' ');

const summary = (item: ApartmentRecommendationItemDetail): string => {
  const parts: string[] = [];
  if (item.builtinKind === 'airflow') parts.push('podformularz dopływu (tablet)');
  if (item.devicesMode !== 'none') {
    parts.push(`urządzenia: ${MODE_LABELS[item.devicesMode]}, ${item.devicePosition === 'suffix' ? 'po treści' : 'przed treścią'}`);
  }
  if (item.roomsMode !== 'none') {
    parts.push(`pomieszczenia: ${MODE_LABELS[item.roomsMode]}${item.roomIds ? ` (${item.roomIds.length})` : ''}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'sama treść';
};

export const ApartmentRecommendationItemsEditor: React.FC = () => {
  const [items, setItems] = useState<ApartmentRecommendationItemDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /** Serwer ostrzegł o podobnej treści — drugi klik zapisuje mimo to. */
  const [overlapWarning, setOverlapWarning] = useState<string | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState<ApartmentRecommendationItemDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await recommendationsApi.getApartmentItems();
      setItems(data.items ?? []);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Nie udało się pobrać zaleceń lokali');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const replaceItem = (updated: ApartmentRecommendationItemDetail) =>
    setItems((prev) =>
      prev.some((i) => i.id === updated.id) ? prev.map((i) => (i.id === updated.id ? updated : i)) : [...prev, updated]);

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Nie udało się zapisać zmiany');
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, delta: -1 | 1) =>
    run(async () => {
      const ids = items.map((i) => i.id);
      const target = index + delta;
      if (target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      const res = await recommendationsApi.reorderApartmentItems(ids);
      setItems(res.items);
    });

  const setActive = (item: ApartmentRecommendationItemDetail, active: boolean) =>
    run(async () => {
      const updated = await recommendationsApi.updateApartmentItem(item.id, { active });
      replaceItem(updated);
      setNotice(active ? `Przywrócono: ${updated.label}` : `Wycofano: ${updated.label}`);
    });

  const openCreate = () => {
    setFormError(null);
    setOverlapWarning(null);
    setForm(emptyForm());
  };

  const openEdit = (item: ApartmentRecommendationItemDetail) => {
    setFormError(null);
    setOverlapWarning(null);
    setForm({
      id: item.id,
      label: item.label,
      baseText: item.baseText,
      devicesMode: item.devicesMode,
      devicePosition: item.devicePosition,
      allowOtherDevice: item.allowOtherDevice,
      roomsMode: item.roomsMode,
      roomIds: item.roomIds,
      allowOtherRoom: item.allowOtherRoom,
      legacyTexts: item.legacyTexts,
      builtinKind: item.builtinKind,
    });
  };

  const submitForm = async () => {
    if (!form || busy) return;
    const label = form.label.trim();
    const baseText = form.baseText.trim();
    if (!label || !baseText) {
      setFormError('Podaj nazwę i treść zalecenia');
      return;
    }
    if (form.roomsMode !== 'none' && form.roomIds !== null && form.roomIds.length === 0) {
      setFormError('Zaznacz co najmniej jedno pomieszczenie albo „Wszystkie"');
      return;
    }
    const payload = {
      label,
      baseText,
      devicesMode: form.devicesMode,
      devicePosition: form.devicePosition,
      allowOtherDevice: form.allowOtherDevice,
      roomsMode: form.roomsMode,
      // bez wyboru pomieszczeń podzbiór nie ma znaczenia — null (serwer odrzuca pustą listę)
      roomIds: form.roomsMode === 'none' ? null : form.roomIds,
      allowOtherRoom: form.allowOtherRoom,
      // Drugi klik po ostrzeżeniu o podobnej treści = świadomy zapis.
      ...(overlapWarning ? { allowOverlap: true } : {}),
    };
    setBusy(true);
    setFormError(null);
    try {
      const saved = form.id
        ? await recommendationsApi.updateApartmentItem(form.id, payload)
        : await recommendationsApi.createApartmentItem(payload);
      replaceItem(saved);
      setNotice(form.id ? `Zapisano: ${saved.label}` : `Dodano: ${saved.label}`);
      setForm(null);
      setOverlapWarning(null);
    } catch (e) {
      if (e instanceof ApartmentItemError && e.code === 'TEXT_OVERLAP') {
        setOverlapWarning(e.message);
      } else {
        setFormError(e instanceof Error ? e.message : 'Nie udało się zapisać zalecenia');
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleRoom = (id: string) => {
    if (!form) return;
    const current = form.roomIds ?? ROOM_OPTIONS.map((r) => r.id);
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    setForm({ ...form, roomIds: next });
  };

  return (
    <section aria-labelledby="apartment-recommendations-heading" style={{ marginTop: 'var(--space-6)' }}>
      <h2
        id="apartment-recommendations-heading"
        style={{ fontSize: 'var(--text-h2, 22px)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}
      >
        Zalecenia dla lokali
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', maxWidth: 760 }}>
        Pozycje w oknie zaleceń lokalu na tablecie (osobno od rubryk A/B/C/D budynku). Możesz dodać
        zalecenie, zmienić nazwę, treść i pola wyboru, ułożyć kolejność albo wycofać pozycję z listy.
        Zalecenia już zapisane w lokalach (i podpisane) zostają bez zmian — także po zmianie treści.
        Tablet pobiera katalog przy otwarciu okna zaleceń i przy „Pobierz paczkę".
      </p>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Ładowanie zaleceń lokali…</p>}
      {loadError && (
        <p role="alert" style={{ color: 'var(--status-deny)' }}>
          {loadError} —{' '}
          <button
            type="button"
            onClick={load}
            style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}
          >
            spróbuj ponownie
          </button>
        </p>
      )}
      {actionError && (
        <p role="alert" style={{ color: 'var(--status-deny)', marginBottom: 'var(--space-3)' }}>
          {actionError}
        </p>
      )}
      {notice && (
        <p role="status" style={{ color: 'var(--status-ok)', marginBottom: 'var(--space-3)' }}>
          {notice}
        </p>
      )}

      {!loading && !loadError && (
        <section
          aria-label="Pozycje zaleceń dla lokali"
          style={{
            border: 'var(--border-width) solid var(--border)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-5)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-3) var(--space-3) var(--space-4)',
              background: 'var(--surface-fill)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>Pozycje ({items.filter((i) => i.active).length} aktywnych)</span>
            <Button variant="ghost" icon="plus" onClick={openCreate} disabled={busy} style={actionStyle}>
              Dodaj zalecenie
            </Button>
          </div>
          {items.map((item, index) => (
            <div
              key={item.id}
              data-testid={`apartment-item-${item.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                borderTop: 'var(--border-width) solid var(--border)',
                padding: 'var(--space-2) var(--space-3) var(--space-2) var(--space-4)',
                opacity: item.active ? 1 : 0.6,
              }}
            >
              <span style={{ flex: 1, minWidth: 0, color: 'var(--text-primary)' }}>
                <strong>{item.label}</strong>
                {!item.active && (
                  <span style={{ color: 'var(--status-deny)', fontSize: 'var(--text-label)', fontWeight: 600 }}>
                    {' '}(wycofane)
                  </span>
                )}
                <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
                  {item.baseText}
                </span>
                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
                  {summary(item)}
                </span>
              </span>
              <Button
                variant="ghost"
                icon="chevron-up"
                onClick={() => move(index, -1)}
                disabled={busy || index === 0}
                aria-label={`Przesuń wyżej: ${item.label}`}
                style={actionStyle}
              >
                Wyżej
              </Button>
              <Button
                variant="ghost"
                icon="chevron-down"
                onClick={() => move(index, 1)}
                disabled={busy || index === items.length - 1}
                aria-label={`Przesuń niżej: ${item.label}`}
                style={actionStyle}
              >
                Niżej
              </Button>
              <Button
                variant="ghost"
                icon="pencil"
                onClick={() => openEdit(item)}
                disabled={busy}
                aria-label={`Zmień zalecenie: ${item.label}`}
                style={actionStyle}
              >
                Zmień
              </Button>
              {item.active ? (
                <Button
                  variant="ghost"
                  icon="ban"
                  onClick={() => setConfirmWithdraw(item)}
                  disabled={busy}
                  aria-label={`Wycofaj zalecenie: ${item.label}`}
                  style={actionStyle}
                >
                  Wycofaj
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  icon="refresh"
                  onClick={() => setActive(item, true)}
                  disabled={busy}
                  aria-label={`Przywróć zalecenie: ${item.label}`}
                  style={actionStyle}
                >
                  Przywróć
                </Button>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Listy urządzeń pozycji, które je pokazują (migr. 054) — kolejność jak w katalogu. */}
      {!loading && !loadError && (
        <DeviceOptionsEditor
          recommendations={items.filter((i) => i.devicesMode !== 'none').map((i) => ({ id: i.id, label: i.label }))}
        />
      )}

      <ModalShell
        open={form !== null}
        title={form?.id ? 'Zmień zalecenie dla lokalu' : 'Nowe zalecenie dla lokalu'}
        onClose={() => { if (!busy) setForm(null); }}
        width={720}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)} disabled={busy}>Anuluj</Button>
            <Button variant="primary" icon="check" onClick={submitForm} disabled={busy}>
              {busy ? 'Zapisywanie…' : overlapWarning ? 'Zapisz mimo to' : form?.id ? 'Zapisz' : 'Dodaj'}
            </Button>
          </>
        }
      >
        {form && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <InputField
              label="Nazwa na liście zaleceń"
              value={form.label}
              maxLength={120}
              autoFocus
              onChange={(e) => {
                // ostrzeżenie o podobnej treści dotyczy sprawdzonego tekstu — po zmianie znika
                setOverlapWarning(null);
                setForm({ ...form, label: singleLine(e.target.value) });
              }}
            />
            <InputField
              label="Treść zalecenia (trafia do protokołu i do CEEB)"
              multiline
              rows={4}
              value={form.baseText}
              maxLength={1000}
              hint={form.devicePosition === 'suffix' && form.devicesMode !== 'none'
                ? 'Urządzenia dopisują się PO treści (np. „…podłączenia: PG, COG").'
                : 'Urządzenia (jeśli są) dopisują się PRZED treścią (np. „PG - …").'}
              onChange={(e) => {
                setOverlapWarning(null);
                setForm({ ...form, baseText: singleLine(e.target.value) });
              }}
            />
            {form.legacyTexts.length > 0 && (
              <div>
                <span style={fieldLabelStyle}>Wcześniejsze treści (rozpoznają stare zapisy, nie trafiają do nowych)</span>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
                  {form.legacyTexts.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
            )}
            {form.builtinKind === 'airflow' && (
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
                Pozycja wbudowana: na tablecie ma podformularz „Urządzenie dopływu jest zamknięte / Ilość",
                więc bez wyboru urządzeń i pomieszczeń.
              </p>
            )}

            <div>
              <label htmlFor="apt-rec-devices" style={fieldLabelStyle}>Wybór urządzeń</label>
              <select
                id="apt-rec-devices"
                value={form.devicesMode}
                disabled={form.builtinKind === 'airflow'}
                onChange={(e) => setForm({ ...form, devicesMode: e.target.value as ApartmentRecommendationMode })}
                style={selectStyle}
              >
                <option value="none">Bez wyboru urządzeń</option>
                <option value="optional">Opcjonalny</option>
                <option value="required">Wymagany</option>
              </select>
            </div>
            {form.devicesMode !== 'none' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                <label style={checkRowStyle}>
                  <input
                    type="radio"
                    name="apt-rec-position"
                    checked={form.devicePosition === 'prefix'}
                    onChange={() => setForm({ ...form, devicePosition: 'prefix' })}
                  />
                  Urządzenia przed treścią („PG, COG - treść")
                </label>
                <label style={checkRowStyle}>
                  <input
                    type="radio"
                    name="apt-rec-position"
                    checked={form.devicePosition === 'suffix'}
                    onChange={() => setForm({ ...form, devicePosition: 'suffix' })}
                  />
                  Urządzenia po treści („treść: PG, COG")
                </label>
                <label style={checkRowStyle}>
                  <input
                    type="checkbox"
                    checked={form.allowOtherDevice}
                    onChange={(e) => setForm({ ...form, allowOtherDevice: e.target.checked })}
                  />
                  Pole „Inne (wpisz)" przy urządzeniach
                </label>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
                  Listę urządzeń tej pozycji edytujesz niżej, w „Typy urządzeń w zaleceniach dla lokali".
                </span>
              </div>
            )}

            <div>
              <label htmlFor="apt-rec-rooms" style={fieldLabelStyle}>Wybór pomieszczeń</label>
              <select
                id="apt-rec-rooms"
                value={form.roomsMode}
                disabled={form.builtinKind === 'airflow'}
                onChange={(e) => setForm({ ...form, roomsMode: e.target.value as ApartmentRecommendationMode })}
                style={selectStyle}
              >
                <option value="none">Bez wyboru pomieszczeń</option>
                <option value="optional">Opcjonalny (bez wyboru zapisuje się sama treść)</option>
                <option value="required">Wymagany</option>
              </select>
            </div>
            {form.roomsMode !== 'none' && (
              <div>
                <label style={checkRowStyle}>
                  <input
                    type="checkbox"
                    checked={form.roomIds === null}
                    onChange={(e) => setForm({ ...form, roomIds: e.target.checked ? null : ROOM_OPTIONS.map((r) => r.id) })}
                  />
                  Wszystkie pomieszczenia ze słownika
                </label>
                {form.roomIds !== null && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0 var(--space-3)' }}>
                    {ROOM_OPTIONS.map((r) => (
                      <label key={r.id} style={checkRowStyle}>
                        <input
                          type="checkbox"
                          checked={form.roomIds!.includes(r.id)}
                          onChange={() => toggleRoom(r.id)}
                        />
                        {r.label}
                      </label>
                    ))}
                  </div>
                )}
                <label style={checkRowStyle}>
                  <input
                    type="checkbox"
                    checked={form.allowOtherRoom}
                    onChange={(e) => setForm({ ...form, allowOtherRoom: e.target.checked })}
                  />
                  Pole „Inne (wpisz)" przy pomieszczeniach
                </label>
              </div>
            )}

            {overlapWarning && (
              <p role="alert" style={{ margin: 0, color: 'var(--status-rec)', fontWeight: 600 }}>
                {overlapWarning} Jeśli to świadomie inna pozycja, kliknij „Zapisz mimo to".
              </p>
            )}
            {formError && (
              <p role="alert" style={{ margin: 0, color: 'var(--status-deny)' }}>{formError}</p>
            )}
          </div>
        )}
      </ModalShell>

      <ConfirmModal
        open={confirmWithdraw !== null}
        title="Wycofać zalecenie?"
        message={
          confirmWithdraw
            ? `„${confirmWithdraw.label}" zniknie z listy zaleceń na tablecie. Zalecenia zapisane wcześniej w lokalach zostaną bez zmian i nadal będą rozpoznawane; pozycję można później przywrócić.`
            : ''
        }
        confirmLabel="Wycofaj"
        destructive
        onCancel={() => setConfirmWithdraw(null)}
        onConfirm={() => {
          const c = confirmWithdraw;
          setConfirmWithdraw(null);
          if (c) setActive(c, false);
        }}
      />
    </section>
  );
};
